import { useEffect, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import TabButton from "../../components/Admin/ApplicationView/TabButton";
import { incentiveClaimApplicationViewSections } from "../../forms/incentiveClaimApplicationFields";
import { getKeyByValue } from "../../utils/utils";
import { useLocation, useParams } from "react-router-dom";
import api from "../../api/ApiService";
import { documentTypeMap } from "../../config/incentiveClaimFormUtils";
import ApplicationSectionRenderer from "../../components/Admin/ApplicationView/ApplicationSectionRenderer";
import {
  installmentsTypes,
  schemeTypes,
} from "../../config/appliedSchemesConfig";
import { useLanguage } from "../../components/LanguageProvider/LanguageProvider";

const ApplicationDraft = () => {
  const [formData, setFormData] = useState({});
  const location = useLocation();
  const { applicationId } = useParams();
  const [userDetails, setUserDetails] = useState({});
  const [incentiveClaimId, setIncentiveClaimId] = useState(null);
  const [installmentType, setInstallmentType] = useState(null);

  const isInterest =
    String(incentiveClaimId) === schemeTypes.interestassistance.value;
  const isReimbursement =
    String(incentiveClaimId) ===
    schemeTypes.sreimbursementcapitalincentive.value;

  const isFirstInstallment = installmentType === installmentsTypes.first.value;
  const isSecondInstallment =
    installmentType === installmentsTypes.second.value;
  const isThirdInstallment = installmentType === installmentsTypes.third.value;
  const { language, setLanguage } = useLanguage();

  // 🔹 Dynamically build and rename tabs
  const dynamicTabs = incentiveClaimApplicationViewSections
    .map((section, i) => {
      let title = section.title;

      if (isInterest && isFirstInstallment) {
        title = language
          ? title.hi.replace("स्टाम्प शुल्क", "सब्सिडी विवरण")
          : title.en.replace("Stamp Duty", "Subsidy Details");
      } else if (isReimbursement && isSecondInstallment) {
        title = title.replace("Stamp Duty", "Tax Documents");
      } else if (isInterest && (isSecondInstallment || isThirdInstallment)) {
        title = language
          ? title.hi.replace("स्टाम्प शुल्क", "सब्सिडी विवरण")
          : title.en.replace("Stamp Duty", "Subsidy Details");
      } else if (isInterest) {
        title = title;
      }

      return {
        key: `section-${i}`,
        label: title,
        section,
      };
    })
    .filter(
      (tab) =>
        !(
          isReimbursement &&
          tab.label.en.toLowerCase().includes("interest subsidy")
        ),
    );

  const [activeTab, setActiveTab] = useState(dynamicTabs[0]?.key || null);

  useEffect(() => {
    const getUserDetails = async () => {
      const resp = await api.get("/user-full-data");
      if (resp.status === 200) {
        setUserDetails(resp.data);
      }
    };
    getUserDetails();
  }, []);

  useEffect(() => {
    const getApplicationData = async () => {
      const [appDetails, responseData, responseDocs] = await Promise.all([
        api.get(`/get-application/${applicationId}`),
        api.get(`/get-caf/${applicationId}`),
        api.post(`/sub-categories/documents`, {
          category_id: 1,
          application_id: applicationId,
          user_id: userDetails?.user_details?.id,
        }),
      ]);

      //const responseData = await api.get(`/get-caf/${applicationId}`);
      const mainData = responseData.data || {};

      // const responseDocs = await api.post(`/sub-categories/documents`, {
      //   category_id: 1,
      //   application_id: applicationId,
      //   user_id: userDetails?.user_details?.id,
      // });

      const docData = {};
      if (responseDocs?.data) {
        responseDocs.data.forEach((doc) => {
          const docTypeId = doc?.id;
          const fieldName = getKeyByValue(documentTypeMap, doc?.name_en);
          if (doc.upload_documents?.length > 0) {
            const uploaded = doc.upload_documents[0];
            docData[fieldName] = {
              url: uploaded.path,
              id: uploaded.id,
              name: uploaded.filename || "",
              label: doc.name_en,
              docTypeId: docTypeId || "",
            };
          } else {
            docData[fieldName] = {
              url: "",
              id: "",
              name: "",
              label: doc.name_en,
              docTypeId: docTypeId || "",
            };
          }
        });
      }

      setIncentiveClaimId(appDetails.invest_claim_id || null);
      setInstallmentType(appDetails.application_type || null);

      let maxAmount = 0;

      if (mainData.area_category === "A") {
        maxAmount = 400000;
      } else if (mainData.area_category === "B") {
        maxAmount = 500000;
      } else if (mainData.area_category === "C") {
        maxAmount = 600000;
      }

      setFormData({
        ...mainData,
        ...docData,
        maximum_eligible_amount_first_installment: maxAmount,
        maximum_eligible_amount_second_installment: maxAmount,
        maximum_eligible_amount_third_installment: maxAmount,
      });
    };
    getApplicationData();
  }, []);

  if (!dynamicTabs.length) {
    return (
      <DashboardLayout pageTitle="Draft Application" mainClass="p-6">
        <p className="text-gray-500">
          {language
            ? "कोई सेक्शन नहीं"
            : "No sections available for this combination."}
        </p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Draft Application" mainClass="p-6">
      {/* 🔹 Tabs */}
      <div className="flex flex-col sm:flex-row sm:space-x-3 space-y-2 sm:space-y-0 p-4 sm:p-6">
        {dynamicTabs.map((tab) => (
          <TabButton
            key={tab.key}
            label={tab.label}
            isActive={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            language={language}
          />
        ))}
      </div>

      <section className="px-6 py-2">
        {dynamicTabs.map(
          (tab) =>
            activeTab === tab.key && (
              <ApplicationSectionRenderer
                key={tab.key}
                section={tab.section}
                formData={formData}
                setFormData={setFormData}
                incentiveClaimId={incentiveClaimId}
                installmentType={installmentType}
                language={language}
              />
            ),
        )}
      </section>
    </DashboardLayout>
  );
};

export default ApplicationDraft;
